import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Card, IconButton, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, useTheme, CircularProgress, Table, TableHead, TableBody, TableRow, TableCell,
  Menu, MenuItem, TextField, Alert, Divider, FormControl, InputLabel, Select, Popover,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ShieldIcon from '@mui/icons-material/Shield';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';
import ProfileWizard, { Owner } from '../components/ProfileWizard';

interface BankConnectionSummary {
  id: string;
  owner_id: string | null;
  institution_name: string | null;
  provider: 'truelayer';
  created_at: string | null;
}

// A small, fixed set of existing theme tokens — cycled deterministically per profile (by id),
// same convention as PropertiesPage.tsx's OWNER_AVATAR_TOKENS, purely so each row's avatar is
// easier to tell apart at a glance without inventing a new palette.
const AVATAR_TOKENS = ['primary', 'accentTeal', 'accentAmber', 'success'] as const;
const avatarToken = (id: string) => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_TOKENS[hash % AVATAR_TOKENS.length];
};

// Click-to-edit text — used for Name (required, empty edits are silently discarded) and VAT
// (optional, empty saves as null). Shared shape rather than two near-identical components.
const InlineEditableText: React.FC<{
  value: string;
  placeholder: string;
  onSave: (v: string | null) => void;
  bold?: boolean;
  allowEmpty?: boolean;
  maxWidth?: number;
}> = ({ value, placeholder, onSave, bold, allowEmpty = true, maxWidth }) => {
  const { palette } = useTheme();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (!trimmed && !allowEmpty) { setDraft(value); return; }
    if (trimmed !== (value || '').trim()) onSave(trimmed || null);
  };

  if (editing) {
    return (
      <TextField
        autoFocus size="small" variant="standard" value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { setDraft(value); setEditing(false); }
        }}
      />
    );
  }
  return (
    <Typography
      onClick={() => setEditing(true)}
      title={maxWidth && value ? value : undefined}
      sx={{
        cursor: 'pointer', fontSize: bold ? '0.9rem' : '0.85rem', fontWeight: bold ? 700 : 400,
        color: value ? palette.tokens.ink : palette.tokens.mutedSoft,
        '&:hover': { textDecoration: 'underline' },
        ...(maxWidth ? { maxWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
      }}
    >
      {value || placeholder}
    </Typography>
  );
};

// Role dropdown — same clickable-chip-with-arrow pattern reused for Type and Country below.
const RoleCell: React.FC<{ owner: Owner; onSave: (patch: { is_owner: boolean; is_manager: boolean }) => void }> = ({ owner, onSave }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const select = (is_owner: boolean, is_manager: boolean) => {
    setAnchorEl(null);
    if (owner.is_owner !== is_owner || owner.is_manager !== is_manager) onSave({ is_owner, is_manager });
  };
  return (
    <>
      <Chip
        size="small" clickable onClick={e => setAnchorEl(e.currentTarget)}
        deleteIcon={<ArrowDropDownIcon />} onDelete={e => setAnchorEl(e.currentTarget as unknown as HTMLElement)}
        label={owner.is_owner && owner.is_manager ? 'Owner & manager' : owner.is_owner ? 'Owner' : 'Manager'}
      />
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => select(true, false)}>Owner only</MenuItem>
        <MenuItem onClick={() => select(false, true)}>Manager only</MenuItem>
        <MenuItem onClick={() => select(true, true)}>Owner & manager</MenuItem>
      </Menu>
    </>
  );
};

const TypeCell: React.FC<{ owner: Owner; onSave: (patch: { entity_type: 'individual' | 'business' }) => void }> = ({ owner, onSave }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const select = (entity_type: 'individual' | 'business') => {
    setAnchorEl(null);
    if (owner.entity_type !== entity_type) onSave({ entity_type });
  };
  return (
    <>
      <Chip
        size="small" clickable variant="outlined" onClick={e => setAnchorEl(e.currentTarget)}
        deleteIcon={<ArrowDropDownIcon />} onDelete={e => setAnchorEl(e.currentTarget as unknown as HTMLElement)}
        label={owner.entity_type} sx={{ textTransform: 'capitalize' }}
      />
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => select('individual')}>Individual</MenuItem>
        <MenuItem onClick={() => select('business')}>Business</MenuItem>
      </Menu>
    </>
  );
};

// UK/Cyprus one-click presets (same two markets used everywhere else in the app, e.g.
// PropertiesPage.tsx) plus "Other…" which drops into a free-text inline edit instead of a menu
// item, since the full ~190-country list isn't worth a dropdown here.
const COUNTRY_QUICK_OPTIONS = ['United Kingdom', 'Cyprus'];
const CountryCell: React.FC<{ owner: Owner; onSave: (patch: { country: string | null }) => void }> = ({ owner, onSave }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [customEditing, setCustomEditing] = useState(false);
  const [draft, setDraft] = useState(owner.country || '');
  useEffect(() => { setDraft(owner.country || ''); }, [owner.country]);

  const select = (country: string | null) => {
    setAnchorEl(null);
    if (owner.country !== country) onSave({ country });
  };
  const commitCustom = () => {
    setCustomEditing(false);
    const v = draft.trim() || null;
    if (v !== owner.country) onSave({ country: v });
  };

  if (customEditing) {
    return (
      <TextField
        autoFocus size="small" variant="standard" value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commitCustom}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); commitCustom(); }
          if (e.key === 'Escape') { setDraft(owner.country || ''); setCustomEditing(false); }
        }}
      />
    );
  }
  return (
    <>
      <Chip
        size="small" clickable variant={owner.country ? 'filled' : 'outlined'} onClick={e => setAnchorEl(e.currentTarget)}
        deleteIcon={<ArrowDropDownIcon />} onDelete={e => setAnchorEl(e.currentTarget as unknown as HTMLElement)}
        label={owner.country || 'Not set'} title={owner.country || undefined}
        sx={{ maxWidth: 150, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
      />
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        {COUNTRY_QUICK_OPTIONS.map(c => <MenuItem key={c} onClick={() => select(c)}>{c}</MenuItem>)}
        <MenuItem onClick={() => { setAnchorEl(null); setCustomEditing(true); }}>Other…</MenuItem>
        {owner.country && <MenuItem onClick={() => select(null)}>Clear</MenuItem>}
      </Menu>
    </>
  );
};

// VAT — a small popover to type into (rather than an inline text field that would grow the
// column's width as you type), with the displayed value itself truncated at a fixed width so the
// VAT column's width never changes regardless of how long the number is.
const VAT_DISPLAY_MAX_WIDTH = 140;
const VatCell: React.FC<{ owner: Owner; onSave: (patch: { vat_number: string | null }) => void }> = ({ owner, onSave }) => {
  const { palette } = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState(owner.vat_number || '');
  useEffect(() => { setDraft(owner.vat_number || ''); }, [owner.vat_number]);

  const close = () => { setAnchorEl(null); setDraft(owner.vat_number || ''); };
  const save = () => {
    const v = draft.trim() || null;
    setAnchorEl(null);
    if (v !== owner.vat_number) onSave({ vat_number: v });
  };

  return (
    <>
      <Typography
        onClick={e => setAnchorEl(e.currentTarget)}
        title={owner.vat_number || undefined}
        sx={{
          cursor: 'pointer', fontSize: '0.85rem', maxWidth: VAT_DISPLAY_MAX_WIDTH,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          color: owner.vat_number ? palette.tokens.ink : palette.tokens.primary,
          fontWeight: owner.vat_number ? 400 : 600,
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {owner.vat_number || 'Add'}
      </Typography>
      <Popover
        open={!!anchorEl} anchorEl={anchorEl} onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, width: 260 }}>
          <TextField
            autoFocus size="small" label="VAT number" value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); save(); } if (e.key === 'Escape') close(); }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" onClick={close} sx={{ color: palette.tokens.muted }}>Cancel</Button>
            <Button size="small" variant="contained" onClick={save}>Save</Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

// Contact — shows email/phone if set; when neither is set, shows a clickable "Add contact"
// which opens the same kind of small popover as VatCell, with both fields at once. Each line is
// truncated at a fixed width (full value on hover via title) so a long email/phone can never
// grow the column, same reasoning as VAT_DISPLAY_MAX_WIDTH above.
const CONTACT_DISPLAY_MAX_WIDTH = 200;
const ContactCell: React.FC<{ owner: Owner; onSave: (patch: { email: string | null; phone: string | null }) => void }> = ({ owner, onSave }) => {
  const { palette } = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [emailDraft, setEmailDraft] = useState(owner.email || '');
  const [phoneDraft, setPhoneDraft] = useState(owner.phone || '');
  useEffect(() => { setEmailDraft(owner.email || ''); setPhoneDraft(owner.phone || ''); }, [owner.email, owner.phone]);

  const close = () => {
    setAnchorEl(null);
    setEmailDraft(owner.email || '');
    setPhoneDraft(owner.phone || '');
  };
  const save = () => {
    const email = emailDraft.trim() || null;
    const phone = phoneDraft.trim() || null;
    setAnchorEl(null);
    if (email !== owner.email || phone !== owner.phone) onSave({ email, phone });
  };

  const hasContact = !!(owner.email || owner.phone);
  const truncateSx = { maxWidth: CONTACT_DISPLAY_MAX_WIDTH, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

  return (
    <>
      {hasContact ? (
        <Box onClick={e => setAnchorEl(e.currentTarget)} sx={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 0.15 }}>
          {owner.email && <Typography title={owner.email} sx={{ fontSize: '0.8rem', color: palette.tokens.muted, ...truncateSx }}>{owner.email}</Typography>}
          {owner.phone && <Typography title={owner.phone} sx={{ fontSize: '0.8rem', color: palette.tokens.muted, ...truncateSx }}>{owner.phone}</Typography>}
        </Box>
      ) : (
        <Typography
          onClick={e => setAnchorEl(e.currentTarget)}
          sx={{ cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: palette.tokens.primary, '&:hover': { textDecoration: 'underline' } }}
        >
          Add
        </Typography>
      )}
      <Popover
        open={!!anchorEl} anchorEl={anchorEl} onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, width: 260 }}>
          <TextField
            autoFocus size="small" label="Email" type="email" value={emailDraft}
            onChange={e => setEmailDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); save(); } if (e.key === 'Escape') close(); }}
          />
          <TextField
            size="small" label="Phone" value={phoneDraft}
            onChange={e => setPhoneDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); save(); } if (e.key === 'Escape') close(); }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" onClick={close} sx={{ color: palette.tokens.muted }}>Cancel</Button>
            <Button size="small" variant="contained" onClick={save}>Save</Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

// "Edit profile" — all fields on one card at once (as opposed to ProfileWizard's one-question-
// at-a-time flow, which is create-only). Opened by the row's pencil icon. Local form state is
// only synced from `owner` when the dialog (re)opens for a given profile — see the effect below
// — so typing doesn't fight with fetchOwners' background refreshes while the dialog is open.
const ProfileDetailsDialog: React.FC<{
  owner: Owner | null;
  onClose: () => void;
  onSave: (ownerId: string, patch: Record<string, unknown>) => Promise<void>;
  bankConnections: BankConnectionSummary[];
  connecting: boolean;
  onConnectBank: (ownerId: string) => void;
  onDisconnectBank: (connection: BankConnectionSummary) => Promise<void>;
}> = ({ owner, onClose, onSave, bankConnections, connecting, onConnectBank, onDisconnectBank }) => {
  const { palette } = useTheme();
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    entity_type: 'individual' as 'individual' | 'business',
    is_owner: true, is_manager: false,
    country: '', vat_number: '',
  });
  const [countryMode, setCountryMode] = useState<'preset' | 'other'>('preset');
  const [saving, setSaving] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState<BankConnectionSummary | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    if (!owner) return;
    setForm({
      name: owner.name, email: owner.email || '', phone: owner.phone || '',
      entity_type: owner.entity_type, is_owner: owner.is_owner, is_manager: owner.is_manager,
      country: owner.country || '', vat_number: owner.vat_number || '',
    });
    setCountryMode(owner.country && !COUNTRY_QUICK_OPTIONS.includes(owner.country) ? 'other' : 'preset');
  }, [owner]);

  if (!owner) return null;

  const roleValue = form.is_owner && form.is_manager ? 'both' : form.is_owner ? 'owner' : 'manager';
  const canSave = form.name.trim().length > 0 && (form.is_owner || form.is_manager);
  const ownerBanks = bankConnections.filter(c => c.owner_id === owner.id);

  const handleConfirmDisconnect = async () => {
    if (!disconnectTarget) return;
    setDisconnecting(true);
    try {
      await onDisconnectBank(disconnectTarget);
      setDisconnectTarget(null);
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave(owner.id, {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        entity_type: form.entity_type,
        is_owner: form.is_owner,
        is_manager: form.is_manager,
        country: form.country || null,
        vat_number: form.entity_type === 'business' ? (form.vat_number.trim() || null) : null,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!owner} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Edit person</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '8px !important' }}>
        <TextField
          label="Name" value={form.name} autoFocus fullWidth sx={{ mt: 1 }}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
        />

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {(['individual', 'business'] as const).map(opt => (
            <Box
              key={opt} onClick={() => setForm(f => ({ ...f, entity_type: opt }))}
              sx={{
                flex: 1, cursor: 'pointer', textAlign: 'center', borderRadius: 2, py: 1,
                border: `1.5px solid ${form.entity_type === opt ? palette.tokens.primary : palette.tokens.hairline}`,
                bgcolor: form.entity_type === opt ? palette.tokens.surfaceSoft : 'transparent',
                fontWeight: 700, fontSize: '0.9rem', color: palette.tokens.ink, textTransform: 'capitalize',
              }}
            >
              {opt}
            </Box>
          ))}
        </Box>

        <FormControl fullWidth size="small">
          <InputLabel>Role</InputLabel>
          <Select
            label="Role" value={roleValue}
            onChange={e => {
              const v = e.target.value;
              setForm(f => ({ ...f, is_owner: v === 'owner' || v === 'both', is_manager: v === 'manager' || v === 'both' }));
            }}
          >
            <MenuItem value="owner">Owner only</MenuItem>
            <MenuItem value="manager">Manager only</MenuItem>
            <MenuItem value="both">Owner & manager</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Country</InputLabel>
            <Select
              label="Country" value={countryMode === 'other' ? 'Other' : form.country} displayEmpty
              onChange={e => {
                const v = e.target.value;
                if (v === 'Other') { setCountryMode('other'); setForm(f => ({ ...f, country: '' })); }
                else { setCountryMode('preset'); setForm(f => ({ ...f, country: v as string })); }
              }}
            >
              <MenuItem value=""><em>Not set</em></MenuItem>
              {COUNTRY_QUICK_OPTIONS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
          {countryMode === 'other' && (
            <TextField
              size="small" label="Country name" value={form.country}
              onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
            />
          )}
        </Box>

        <TextField
          label="Email" type="email" value={form.email} fullWidth
          onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
        />
        <TextField
          label="Phone" value={form.phone} fullWidth
          onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
        />
        {form.entity_type === 'business' && (
          <TextField
            label="VAT number" value={form.vat_number} fullWidth
            onChange={e => setForm(f => ({ ...f, vat_number: e.target.value }))}
          />
        )}

        <Box>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: palette.tokens.mutedSoft, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
            Connected banks
          </Typography>
          {ownerBanks.length === 0 ? (
            <Typography sx={{ fontSize: '0.85rem', color: palette.tokens.mutedSoft, mb: 1.5 }}>
              No banks connected to this person yet.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
              {ownerBanks.map(conn => (
                <Box
                  key={conn.id}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    border: `1px solid ${palette.tokens.hairline}`, borderRadius: 2, px: 1.5, py: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountBalanceIcon sx={{ fontSize: 16, color: palette.tokens.mutedSoft }} />
                    <Typography sx={{ fontSize: '0.85rem', color: palette.tokens.ink }}>
                      {conn.institution_name || 'Connected bank'}
                    </Typography>
                  </Box>
                  <Button size="small" color="error" onClick={() => setDisconnectTarget(conn)} sx={{ fontSize: '0.75rem' }}>
                    Disconnect
                  </Button>
                </Box>
              ))}
            </Box>
          )}
          <Button
            size="small" variant="outlined" disabled={connecting} onClick={() => onConnectBank(owner.id)}
            sx={{ borderRadius: 2 }}
          >
            {connecting ? 'Connecting…' : 'Add another bank'}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: palette.tokens.muted }}>Cancel</Button>
        <Button variant="contained" disabled={!canSave || saving} onClick={handleSave} sx={{ borderRadius: 2 }}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>

      <Dialog open={!!disconnectTarget} onClose={() => setDisconnectTarget(null)}>
        <DialogTitle>Disconnect bank?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: palette.tokens.muted }}>
            "{disconnectTarget?.institution_name || 'This bank'}" will be fully disconnected — not just
            unassigned from this person. You'll need to reconnect it from scratch if you want its
            transactions back.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDisconnectTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" disabled={disconnecting} onClick={handleConfirmDisconnect}>
            {disconnecting ? 'Disconnecting…' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

const PeoplePage: React.FC = () => {
  const { palette } = useTheme();
  const { token, user } = useAuth();

  const [owners, setOwners] = useState<Owner[]>([]);
  const [bankConnections, setBankConnections] = useState<BankConnectionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [detailsTarget, setDetailsTarget] = useState<Owner | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Owner | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ── inline TrueLayer connect flow ("Connect" in the Bank column, and the wizard's own
  // "connect a bank now" step) — same consent/MFA-aware flow AccountingPage's "Connect bank"
  // button uses, ported here so choosing it never navigates the user away from this page.
  const [connecting, setConnecting] = useState(false);
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  const [acceptingConsent, setAcceptingConsent] = useState(false);
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaEnrollDialogOpen, setMfaEnrollDialogOpen] = useState(false);
  const [mfaEnrolling, setMfaEnrolling] = useState(false);
  const [mfaQrCode, setMfaQrCode] = useState<string | null>(null);
  const [mfaSecret, setMfaSecret] = useState<string | null>(null);
  const [mfaConfirmCode, setMfaConfirmCode] = useState('');
  const [mfaConfirming, setMfaConfirming] = useState(false);
  const [mfaEnrollError, setMfaEnrollError] = useState<string | null>(null);
  // Which profile the in-flight connect flow is for — consumed once the OAuth tab reports back,
  // to auto-assign the newly created connection to this profile (same PATCH the "Manage banks"
  // owner dropdown on Accounting uses).
  const pendingBankOwnerIdRef = useRef<string | null>(null);

  const headers = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  const fetchOwners = useCallback(async (silent = false) => {
    if (!token) return;
    if (!silent) setLoading(true);
    try {
      const [ownersRes, connectionsRes] = await Promise.all([
        fetch(`${config.apiUrl}/api/accounting/owners`, { headers: headers() }),
        fetch(`${config.apiUrl}/api/truelayer/connections`, { headers: headers() }),
      ]);
      if (ownersRes.ok) {
        const d = await ownersRes.json();
        setOwners(d.owners || []);
      }
      if (connectionsRes.ok) {
        const d = await connectionsRes.json();
        setBankConnections(d.connections || []);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token, headers]);

  useEffect(() => { fetchOwners(); }, [fetchOwners]);

  const bankCountFor = (ownerId: string) => bankConnections.filter(c => c.owner_id === ownerId).length;

  const handleOpenNew = () => setWizardOpen(true);
  const handleOpenEdit = (owner: Owner) => setDetailsTarget(owner);

  // Starts (or resumes, with an MFA code) the TrueLayer connect flow for a given profile, right
  // here on this page — opens the bank's OAuth page in a new tab, same as AccountingPage's
  // "Connect bank" button. Stashes the profile id so the postMessage listener effect below can
  // auto-assign the resulting connection to it once the OAuth tab reports completion.
  const handleConnectBank = async (ownerId: string, mfaCodeOverride?: string) => {
    pendingBankOwnerIdRef.current = ownerId;
    setConnecting(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/auth/truelayer/start`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify(mfaCodeOverride ? { mfa_code: mfaCodeOverride } : {}),
      });
      const data = await res.json();
      if (res.ok) {
        window.open(data.auth_url, '_blank');
        setMfaDialogOpen(false);
        setMfaCode('');
        setMfaError(null);
        return;
      }
      if (data.consent_required) { setConsentDialogOpen(true); return; }
      if (data.mfa_enrollment_required) { setMfaEnrollDialogOpen(true); return; }
      if (data.mfa_code_required) { setMfaError(null); setMfaDialogOpen(true); return; }
      setMfaError(data.error || 'Failed to connect bank');
      setMfaDialogOpen(true);
    } finally {
      setConnecting(false);
    }
  };

  const handleAcceptBankConsent = async () => {
    if (!pendingBankOwnerIdRef.current) return;
    setAcceptingConsent(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/users/consent/bank-data`, { method: 'POST', headers: headers() });
      if (res.ok) {
        setConsentDialogOpen(false);
        handleConnectBank(pendingBankOwnerIdRef.current);
      }
    } finally {
      setAcceptingConsent(false);
    }
  };

  const handleStartMfaEnroll = async () => {
    setMfaEnrollError(null);
    setMfaEnrolling(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/mfa/enroll`, { method: 'POST', headers: headers(), body: JSON.stringify({}) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start MFA enrollment');
      setMfaQrCode(data.qr_code);
      setMfaSecret(data.secret);
    } catch (err: any) {
      setMfaEnrollError(err.message || 'Failed to start MFA enrollment');
    } finally {
      setMfaEnrolling(false);
    }
  };

  const handleConfirmMfaEnroll = async () => {
    if (!mfaConfirmCode.trim() || !pendingBankOwnerIdRef.current) return;
    setMfaEnrollError(null);
    setMfaConfirming(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/mfa/confirm`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ code: mfaConfirmCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid code');
      setMfaEnrollDialogOpen(false);
      setMfaQrCode(null);
      setMfaSecret(null);
      setMfaConfirmCode('');
      handleConnectBank(pendingBankOwnerIdRef.current);
    } catch (err: any) {
      setMfaEnrollError(err.message || 'Invalid code');
    } finally {
      setMfaConfirming(false);
    }
  };

  // Once the OAuth tab reports completion (postMessage, same as AccountingPage), find the newest
  // still-unassigned connection — the one that was just created, since connecting itself always
  // stays unscoped — and assign it to whichever profile started this flow.
  useEffect(() => {
    const assignPendingOwnerIfAny = async () => {
      const ownerId = pendingBankOwnerIdRef.current;
      if (!ownerId) return;
      const res = await fetch(`${config.apiUrl}/api/truelayer/connections`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        const connections: BankConnectionSummary[] = data.connections || [];
        const newest = connections
          .filter(c => !c.owner_id)
          .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0];
        if (newest) {
          await fetch(`${config.apiUrl}/api/${newest.provider}/connections/${newest.id}/owner`, {
            method: 'PATCH', headers: headers(), body: JSON.stringify({ owner_id: ownerId }),
          });
        }
      }
      pendingBankOwnerIdRef.current = null;
      fetchOwners(true);
    };
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'truelayer-auth-complete') assignPendingOwnerIfAny();
    };
    window.addEventListener('message', handleMessage);
    window.addEventListener('focus', assignPendingOwnerIfAny);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('focus', assignPendingOwnerIfAny);
    };
  }, [headers, fetchOwners]);

  // Called once the wizard (create-only — editing uses ProfileDetailsDialog below instead) has
  // POSTed the new profile. If the user chose "connect a bank now", start the same inline connect
  // flow the table's own "Connect" button uses — no navigation away.
  const handleWizardSaved = (owner: Owner, connectBankNow: boolean) => {
    setWizardOpen(false);
    fetchOwners(true);
    if (connectBankNow) handleConnectBank(owner.id);
  };

  // Generic single-field(s) save for the inline-editable/dropdown cells below — same PUT the
  // wizard's edit mode uses, just with a partial patch instead of the full form.
  const updateOwner = async (ownerId: string, patch: Record<string, unknown>) => {
    await fetch(`${config.apiUrl}/api/accounting/owners/${ownerId}`, {
      method: 'PUT', headers: headers(), body: JSON.stringify(patch),
    });
    fetchOwners(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await fetch(`${config.apiUrl}/api/accounting/owners/${deleteTarget.id}`, { method: 'DELETE', headers: headers() });
      setDeleteTarget(null);
      fetchOwners(true);
    } finally {
      setDeleting(false);
    }
  };

  // Disconnects a bank connection entirely (not just unassigning it from this profile) — same
  // DELETE AccountingPage's "Disconnect" action uses. Only reachable from ProfileDetailsDialog's
  // "Connected banks" list.
  const handleDisconnectBank = async (connection: BankConnectionSummary) => {
    await fetch(`${config.apiUrl}/api/${connection.provider}/connections/${connection.id}`, {
      method: 'DELETE', headers: headers(),
    });
    fetchOwners(true);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1600, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, color: palette.tokens.ink }}>People</Typography>
          <Typography sx={{ color: palette.tokens.muted, mt: 0.5, whiteSpace: 'nowrap' }}>
            Individuals or businesses that own and/or manage properties in your portfolio.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenNew} sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}>
          New person
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
      ) : owners.length === 0 ? (
        <Card elevation={0} sx={{ border: `1px solid ${palette.tokens.hairline}`, borderRadius: 3, p: 5, textAlign: 'center' }}>
          <Typography sx={{ color: palette.tokens.muted }}>
            No people yet. Create one for yourself, a family member, or a letting agency/business.
          </Typography>
        </Card>
      ) : (
        <Card elevation={0} sx={{ border: `1px solid ${palette.tokens.hairline}`, borderRadius: 3, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: palette.tokens.mutedSoft, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, bgcolor: palette.tokens.surfaceSoft, textAlign: 'center' } }}>
                <TableCell sx={{ width: 260, maxWidth: 260 }}>Name</TableCell>
                <TableCell sx={{ width: 130, maxWidth: 130 }}>Type</TableCell>
                <TableCell sx={{ width: 260, maxWidth: 260, pl: 8 }}>Role</TableCell>
                <TableCell sx={{ width: 170, maxWidth: 170, pl: 8 }}>Country</TableCell>
                <TableCell sx={{ width: 220, maxWidth: 220, pl: 10 }}>VAT</TableCell>
                <TableCell sx={{ width: 260, maxWidth: 260, pl: 4 }}>Contact</TableCell>
                <TableCell>Bank</TableCell>
                <TableCell sx={{ width: 80, maxWidth: 80 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {owners.map(owner => {
                const tokenKey = avatarToken(owner.id);
                const bankCount = bankCountFor(owner.id);
                return (
                  <TableRow key={owner.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell sx={{ width: 260, maxWidth: 260 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Box sx={{
                          width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', bgcolor: (palette.tokens as any)[tokenKey], color: palette.tokens.white, flexShrink: 0,
                        }}>
                          {owner.entity_type === 'business' ? <BusinessIcon sx={{ fontSize: 16 }} /> : <PersonIcon sx={{ fontSize: 16 }} />}
                        </Box>
                        <InlineEditableText
                          value={owner.name} placeholder="Name" bold allowEmpty={false} maxWidth={210}
                          onSave={(v) => v && updateOwner(owner.id, { name: v })}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 130, maxWidth: 130 }}>
                      <TypeCell owner={owner} onSave={(patch) => updateOwner(owner.id, patch)} />
                    </TableCell>
                    <TableCell align="center" sx={{ width: 260, maxWidth: 260, pl: 8 }}>
                      <RoleCell owner={owner} onSave={(patch) => updateOwner(owner.id, patch)} />
                    </TableCell>
                    <TableCell align="center" sx={{ width: 170, maxWidth: 170, pl: 8 }}>
                      <CountryCell owner={owner} onSave={(patch) => updateOwner(owner.id, patch)} />
                    </TableCell>
                    <TableCell align="center" sx={{ width: 220, maxWidth: 220, pl: 10 }}>
                      <VatCell owner={owner} onSave={(patch) => updateOwner(owner.id, patch)} />
                    </TableCell>
                    <TableCell align="center" sx={{ width: 260, maxWidth: 260, pl: 4 }}>
                      <ContactCell owner={owner} onSave={(patch) => updateOwner(owner.id, patch)} />
                    </TableCell>
                    <TableCell align="center">
                      {bankCount > 0 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <AccountBalanceIcon sx={{ fontSize: 15, color: palette.tokens.mutedSoft }} />
                          <Typography sx={{ fontSize: '0.8rem', color: palette.tokens.mutedSoft, whiteSpace: 'nowrap' }}>
                            {bankCount} connected
                          </Typography>
                        </Box>
                      ) : (
                        <Button
                          size="small" disabled={connecting} onClick={() => handleConnectBank(owner.id)}
                          sx={{ fontSize: '0.85rem', fontWeight: 600, minWidth: 0, px: 0, color: palette.tokens.primary }}
                        >
                          Connect
                        </Button>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ width: 80, maxWidth: 80, whiteSpace: 'nowrap', px: 0.5 }}>
                      <IconButton size="small" onClick={() => handleOpenEdit(owner)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => setDeleteTarget(owner)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <ProfileWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSaved={handleWizardSaved}
        editTarget={null}
        suggestedIndividualName={user?.name || ''}
        suggestedEmail={user?.email || ''}
        token={token}
      />

      <ProfileDetailsDialog
        owner={detailsTarget}
        onClose={() => setDetailsTarget(null)}
        onSave={updateOwner}
        bankConnections={bankConnections}
        connecting={connecting}
        onConnectBank={handleConnectBank}
        onDisconnectBank={handleDisconnectBank}
      />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete person?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: palette.tokens.muted }}>
            "{deleteTarget?.name}" will be removed. Any flats or bank connections assigned to it will become
            unassigned — nothing else is deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" disabled={deleting} onClick={handleDelete}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bank data consent ── */}
      <Dialog open={consentDialogOpen} onClose={() => setConsentDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Connect your bank</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: palette.tokens.muted }}>
            By connecting your bank, you consent to MillOS collecting and storing your transaction data via
            TrueLayer. Transaction data is encrypted at rest and retained for up to 24 months. See our{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> for details.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConsentDialogOpen(false)} sx={{ color: palette.tokens.muted }}>Cancel</Button>
          <Button variant="contained" onClick={handleAcceptBankConsent} disabled={acceptingConsent} sx={{ borderRadius: 2 }}>
            {acceptingConsent ? 'Saving…' : 'I agree'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── MFA code ── */}
      <Dialog open={mfaDialogOpen} onClose={() => setMfaDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Verify it's you</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: palette.tokens.muted, mb: 2 }}>
            Enter the 6-digit code from your authenticator app to connect your bank.
          </Typography>
          {mfaError && <Typography variant="body2" sx={{ color: palette.tokens.error, mb: 1 }}>{mfaError}</Typography>}
          <TextField
            size="small" label="6-digit code" value={mfaCode} onChange={e => setMfaCode(e.target.value)}
            fullWidth autoFocus disabled={connecting}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setMfaDialogOpen(false)} sx={{ color: palette.tokens.muted }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => pendingBankOwnerIdRef.current && handleConnectBank(pendingBankOwnerIdRef.current, mfaCode)}
            disabled={connecting || !mfaCode.trim()} sx={{ borderRadius: 2 }}
          >
            {connecting ? 'Verifying…' : 'Verify'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Inline "Set up 2FA" — same QR/confirm steps as ProfilePage.tsx's Two-Factor
          Authentication card, surfaced here so connecting a bank never requires leaving this page. ── */}
      <Dialog
        open={mfaEnrollDialogOpen}
        onClose={() => { setMfaEnrollDialogOpen(false); setMfaQrCode(null); setMfaSecret(null); setMfaConfirmCode(''); setMfaEnrollError(null); }}
        maxWidth="xs" fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <ShieldIcon sx={{ color: palette.tokens.primary }} />
          Two-Factor Authentication
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Required before connecting a bank account. Uses an authenticator app (e.g. Google Authenticator, Authy).
          </Typography>
          {mfaEnrollError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMfaEnrollError(null)}>{mfaEnrollError}</Alert>
          )}
          {mfaQrCode ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Step 1 of 2 — scan this with your authenticator app
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 2 }}>
                <img src={mfaQrCode} alt="MFA QR code" style={{ width: 160, height: 160, border: `1px solid ${palette.tokens.hairline}`, borderRadius: 8 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Can't scan it? Type this code instead:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{mfaSecret}</Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Step 2 of 2 — open the account you just added in your authenticator app and enter the 6-digit code it's showing
              </Typography>
              <TextField
                size="small" label="6-digit code" value={mfaConfirmCode} onChange={e => setMfaConfirmCode(e.target.value)}
                disabled={mfaConfirming} autoFocus fullWidth
              />
            </Box>
          ) : (
            <Button
              variant="outlined" onClick={handleStartMfaEnroll} disabled={mfaEnrolling}
              startIcon={mfaEnrolling ? <CircularProgress size={16} /> : <ShieldIcon />} sx={{ borderRadius: 2 }}
            >
              {mfaEnrolling ? 'Starting…' : 'Set up'}
            </Button>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => { setMfaEnrollDialogOpen(false); setMfaQrCode(null); setMfaSecret(null); setMfaConfirmCode(''); setMfaEnrollError(null); }}
            sx={{ color: palette.tokens.muted }}
          >
            Cancel
          </Button>
          {mfaQrCode && (
            <Button
              variant="contained" onClick={handleConfirmMfaEnroll} disabled={mfaConfirming || !mfaConfirmCode.trim()}
              startIcon={mfaConfirming ? <CircularProgress size={16} /> : undefined} sx={{ borderRadius: 2 }}
            >
              {mfaConfirming ? 'Verifying…' : 'Confirm'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PeoplePage;
